import { AnalysisExecution } from '@/components/AnalysisHistory/ColumnDef'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { toast } from 'vue-sonner'
import Papa from 'papaparse';


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text)
  toast('Copied to clipboard')
}

export function toTitleCase(str: string) {
  if (!str) return '';
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
  )
}

export function getDuration(
  startDateTime: string | number | null,
  endDateTimeString: string | number | null
): string | null {
  // If startDateTime is null, we can't compute a duration.
  if (startDateTime === null) {
    return "--:--";
  }

  // Parse the start date.
  const start = new Date(startDateTime);
  if (isNaN(start.getTime())) {
    return "--:--";
  }

  // If endDateTimeString is provided, parse it.
  // Otherwise, use the current time (UTC now).
  let end: Date;
  if (endDateTimeString !== null) {
    end = new Date(endDateTimeString);
    if (isNaN(end.getTime())) {
      end = new Date();
    }
  } else {
    end = new Date();
  }

  // Calculate the difference in milliseconds.
  let diffInMs = end.getTime() - start.getTime();

  // Optionally, if the difference is negative, you can handle it here.
  // For now, we'll clamp it to 0.
  if (diffInMs < 0) {
    diffInMs = 0;
  }

  // Convert the difference from milliseconds to seconds.
  const totalSeconds = Math.floor(diffInMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // Helper function to pad numbers with leading zeros.
  const pad = (num: number) => num.toString().padStart(2, '0');

  // Return the duration in the format hh:mm:ss. (use format duration to shorten it when displaying)  
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}


export function getLocalizedDateTimeString(datetimeString: string | number | null) {
  if (datetimeString === null || datetimeString === 'None' || datetimeString === undefined) {
    return '--:--';
  }

  let dateObject: Date;

  if (typeof datetimeString === 'number') {
    // If it's a number, don't wrap it in a string.
    dateObject = new Date(datetimeString);
  } else {
    // If it's a string, make it ISO 8601 compliant by replacing the space with 'T'
    const compliantDateTimeString = datetimeString.replace(' ', 'T');
    dateObject = new Date(compliantDateTimeString);
  }

  const localizedString = dateObject.toLocaleString(navigator.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short'
  });

  const sections = localizedString.split(",")
  if (sections.length == 3) {
    const result = `${sections[0]}, ${sections[1]} at ${sections[2]}`
    return result
  }



  return localizedString;
}


export function getUTCDateTimeString(datetimeString: string | null) {
  if (datetimeString === null || datetimeString === 'None' || datetimeString === undefined) {
    return '--:--'
  }
  // Make the string ISO 8601 compliant by replacing the space with 'T'
  const compliantDateTimeString = `${datetimeString}`.replace(' ', 'T')

  // Convert to Date object
  const dateObject = new Date(compliantDateTimeString)

  // Localize based on the browser's locale
  // You can customize 'locales' and 'options' parameters to control the output further

  const localizedString = dateObject.toUTCString()

  return localizedString
}

export function getLocalizedDateTimeString2(
  datetimeStringPrimary: string | null,
  datetimeStringSecondary: string | null
) {
  if (datetimeStringPrimary === null || datetimeStringPrimary === 'None')
    return getLocalizedDateTimeString(datetimeStringSecondary)
  return getLocalizedDateTimeString(datetimeStringPrimary)
}

export function toShortString(str: string, length: number) {

  if (!str) return str;

  if (str.length > length) {
    return str.substring(0, length - 3) + '...'
  }
  return str
}

export function toTruncatedString(str: string, length: number) {

  if (!str) return str;

  if (str.length > length) {
    const start = str.substring(0, length - 10) + '...'
    const end = str.substring(str.length - 7)
    return `${start}${end}`
  }
  return str
}

export function formatDuration(durationStr: string | null) {


  if (durationStr === null || durationStr === 'NA' || durationStr === undefined) {
    return '--:--'
  }
  // Split the string by colon and period to extract hours, minutes, seconds, and milliseconds
  const parts = durationStr.split(/[:.]/)
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  const seconds = parseInt(parts[2], 10)

  // Construct the output string
  const result = formatTime(hours, minutes, seconds, 0);

  if (result.toLowerCase().includes("nan")) {
    return "--:--"
  }

  return result
}

export function formatTime(hours: number, minutes: number, seconds: number, milliseconds: number) {

  let result = ''
  if (hours > 0) result += `${pad(hours)}:`
  result += `${pad(minutes)}:`;
  result += `${pad(seconds)}`;
  if (milliseconds > 0) {
    result += `.${pad(seconds, 3)}`;
  }

  return result;

}

export function pad(value: number, digits = 2) {
  return value.toString().padStart(digits, "0");
}

export function transformPayloadToAuditArray(payload: AnalysisExecution) {
  try {
    const records = [
      { name: 'ID', value: payload.id },
      { name: 'Email', value: payload.user_email },
      { name: 'Name', value: payload.user_full_name },
      { name: 'Company Name', value: payload.company_name },
      { name: 'Date of Analysis', value: payload.start_utc },
      { name: 'Start Time of Analysis', value: payload.start_utc },
      { name: 'Engine Version', value: payload.execution_engine_version },
      { name: 'Api Version', value: payload.api_version },
      { name: 'Analysis time', value: payload.elapsed },
      { name: 'Analysis Input file', value: payload.analysis_file_name },
      { name: 'Input file ID', value: payload.analysis_file_id }
    ]

    return records.map((item) => `${item.name}: ${item.value}`)
  } catch (err) {
    console.error('Failed to load data: ', err)
    return []
  }
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function bytesToMBs(bytes: number | string) {
  return Number(bytes) / (1024 * 1024)
}

export function mbsToBytes(mb: number | string) {
  return Number(mb) * 1024 * 1024
}

export function bytesToKBs(bytes: number | string) {
  return Number(bytes) / 1024
}

/**
 * Formats bytes to human-readable file size with appropriate unit (KB, MB, GB, TB)
 * @param bytes - Size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with size and unit (e.g., "125 MB", "1.5 GB")
 */
export function formatFileSize(bytes: number | string, decimals: number = 2): string {
  const numBytes = Number(bytes)
  
  if (numBytes === 0) return '0 Bytes'
  
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  
  const i = Math.floor(Math.log(numBytes) / Math.log(k))
  const size = parseFloat((numBytes / Math.pow(k, i)).toFixed(dm))
  
  // Remove unnecessary trailing zeros and decimal point
  const formattedSize = size % 1 === 0 ? size.toFixed(0) : size.toString()
  
  return `${formattedSize} ${sizes[i]}`
}



type jsonResponse = { column_names: string[]; data: string[][] };

export function parseFromApi(jsonRes: jsonResponse) {

  let res = Papa.parse(
    Papa.unparse({
      fields: jsonRes.column_names,
      data: jsonRes.data,
    }),
    {
      header: true,
      dynamicTyping: true,
    }
  );
  return res;
}


export const colorMap = {
  active: 'bg-brand-green hover:bg-brand-green',
  cancelled: 'bg-[#ff5757] hover:bg-[#ff5757]',
  complete: 'bg-brand-green hover:bg-brand-green',
  disabled: 'bg-[#ff5757] hover:bg-[#ff5757]',
  enabled: 'bg-brand-green hover:bg-brand-green',
  expired: 'bg-[#ff5757] hover:bg-[#ff5757]',
  fail: 'bg-[#ff5757] hover:bg-[#ff5757]',
  failed: 'bg-[#ff5757] hover:bg-[#ff5757]',
  'generating individual plots': 'bg-blue-500 hover:bg-blue-500 text-white',
  'generating summaries': 'bg-blue-500 text-white',
  inactive: 'bg-orange-500',
  missing: 'bg-yellow-500',
  pass: 'bg-brand-green hover:bg-brand-green',
  pending: 'bg-orange-500',
  processing: 'bg-blue-500 hover:bg-blue-500 text-white',
  queuing: 'bg-yellow-500',
  restricted: 'bg-orange-500',
  roles: 'bg-gray-500',
  submitting: 'bg-orange-500',
  suspended: 'bg-[#ff5757] hover:bg-[#ff5757]',
  "new": 'bg-brand-green hover:bg-brand-green',
  "alpha": 'bg-orange-500 hover:bg-orange-500 text-white',
  "beta": 'bg-blue-500 hover:bg-blue-500 text-white',
  "live": 'bg-brand-green hover:bg-brand-green',
};


export function searchColorMap(find: string, defaultValue: string = "bg-blue-500 hover:bg-blue-500 text-white") {
  const value = colorMap[find as keyof typeof colorMap]
  if (!value) {
    return defaultValue
  }
  return value
}

export const defaultMessageDurationSeconds: number = 1;

export const parseSmartValue = (value: string): boolean | number | string => {
  const boolParsed = tryParseBooleanValue(value)
  if (typeof boolParsed === 'boolean') return boolParsed

  const numberParsed = tryParseNumberValue(boolParsed)
  if (typeof numberParsed === 'number') return numberParsed

  return value
}

export const tryParseBooleanValue = (value: string): boolean | string => {
  const trimmed = (value ?? '').trim().toLowerCase()
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false
  return value
}

export const tryParseNumberValue = (value: string): string | number => {

  const trimmedValue = value ?? ''.trim()
  // Regex for matching valid numbers (integers, decimals, negatives)
  const isNumeric = /^-?\d+(\.\d+)?$/.test(trimmedValue)
  const parsedValue = isNumeric ? Number(trimmedValue) : trimmedValue
  return parsedValue

}

export const destructureWithOmit = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
  const result = { ...obj };
  for (const key of keys) {
    delete result[key];
  }
  return result;
};

export const keepOnlyKeys = <T extends Record<string, any>, K extends keyof T>(obj: T, keysToKeep: K[]): void => {
  Object.keys(obj).forEach((key) => {
    if (!keysToKeep.includes(key as K)) {
      delete obj[key as keyof T];
    }
  });
};

export const isEmptyOrWhitespace = (input: string | null | undefined): boolean => {
    return typeof input !== 'string' || input.trim().length === 0;
}